/**
 * @lines98/client/lib/i18n/flags — inline SVG flags by ISO-3166 country code.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: ADR-0021 D-8 — флаги в переключателе языка ОБЯЗАНЫ быть
 * SVG, НЕ unicode flag-emoji: Windows/Chrome НЕ рендерят regional-indicator пары как
 * флаги (показывают две буквы). Решение по стеку (ADR-0001 vanilla, bundle <200КБ, no
 * CDN): инлайн-SVG как ДАННЫЕ в этом модуле — ноль рантайм-зависимостей, ноль сетевых
 * запросов (CWV), Windows-safe. Тот же паттерн «текст/иконка = данные», что у
 * lib/brand-logo (единый источник) и Tabler-иконок в nav-rail. Код страны берётся из
 * `LANG_FLAG` (config seo, единый источник, T-I18N-1) — здесь маппинг код→разметка.
 * (owner п.13 2026-06-21: English теперь маппится на флаг США `us`, не `gb`.)
 *
 * Флаги — УПРОЩЁННЫЕ узнаваемые силуэты (полосы / поля / центральный знак), не
 * пиксель-точные гербы: цель — мгновенное узнавание страны в пункте списка, а не
 * вексиллологическая точность. viewBox 0 0 3 2 (соотношение 3:2, канон большинства
 * флагов) → SVG масштабируется ФОРМУЛОЙ из CSS без фикс-px (§0.G.5). preserveAspectRatio
 * по умолчанию (xMidYMid meet) сохраняет пропорции.
 *
 * SCOPE: предоставить готовую SVG-разметку флага по ISO-коду (`flagSvg(code)`).
 * NOT IN SCOPE: размер/позиция флага (CSS nav-lang.css), выбор кода для языка
 *   (`LANG_FLAG` в config seo), сам контрол (lib/nav-lang).
 *
 * Cross-references: ADR-0021 D-8 (SVG не emoji); `@lines98/shared/config/seo` LANG_FLAG;
 *   lib/brand-logo (тот же «иконка = данные» паттерн); lib/nav-lang (потребитель).
 */

/**
 * Обернуть содержимое флага в `<svg>` с зашитым классом-якорем размера
 * (`nav-lang-flag-svg` — CSS управляет шириной/высотой ФОРМУЛОЙ) и `aria-hidden`
 * (имя несёт текст эндонима рядом — нет двойного объявления для скринридера).
 * viewBox 3:2 → масштаб без фикс-px-потолка.
 */
function flag(body: string): string {
  return (
    `<svg class="nav-lang-flag-svg" viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg" ` +
    `aria-hidden="true" focusable="false">${body}</svg>`
  );
}

/** Горизонтальная триколор-полоса (сверху вниз). */
function hStripes(...colors: readonly string[]): string {
  const h = 2 / colors.length;
  return colors
    .map((c, i) => `<rect x="0" y="${(i * h).toFixed(4)}" width="3" height="${h.toFixed(4)}" fill="${c}"/>`)
    .join('');
}

/** Вертикальная триколор-полоса (слева направо). */
function vStripes(...colors: readonly string[]): string {
  const w = 3 / colors.length;
  return colors
    .map((c, i) => `<rect x="${(i * w).toFixed(4)}" y="0" width="${w.toFixed(4)}" height="2" fill="${c}"/>`)
    .join('');
}

/** Сплошное поле. */
function field(color: string): string {
  return `<rect x="0" y="0" width="3" height="2" fill="${color}"/>`;
}

/**
 * SVG-разметка флага по ISO-3166 alpha-2 коду (нижний регистр, как в `LANG_FLAG`).
 * Покрывает коды, используемые `LANG_FLAG` (us/ru/de/es/br/it/fr/cn/jp/kr/tw/vn/tr/id/th);
 * `gb` оставлен как данные (en сейчас маппится на `us`, owner п.13 2026-06-21).
 * Незнакомый код → нейтральный глобус-плейсхолдер (defensive; не должно
 * случаться — `LANG_FLAG` типобезопасен).
 */
const FLAGS: Readonly<Record<string, string>> = {
  // США (en→us, owner п.13 2026-06-21: флаг США вместо британского для English) —
  // упрощённые красно-белые горизонтальные полосы + синий кантон с белыми звёздами.
  // hStripes — тот же примитив полос, что у прочих флагов; кантон = rect + звёзды.
  us: flag(
    `${hStripes('#b22234', '#ffffff', '#b22234', '#ffffff', '#b22234', '#ffffff', '#b22234')}` +
      `<rect x="0" y="0" width="1.2" height="1.0769" fill="#3c3b6e"/>` +
      `<path d="M0.3 0.18 L0.36 0.34 L0.53 0.34 L0.39 0.45 L0.45 0.62 L0.3 0.51 L0.15 0.62 L0.21 0.45 L0.07 0.34 L0.24 0.34 Z" fill="#ffffff"/>` +
      `<path d="M0.9 0.18 L0.96 0.34 L1.13 0.34 L0.99 0.45 L1.05 0.62 L0.9 0.51 L0.75 0.62 L0.81 0.45 L0.67 0.34 L0.84 0.34 Z" fill="#ffffff"/>` +
      `<path d="M0.6 0.66 L0.66 0.82 L0.83 0.82 L0.69 0.93 L0.75 1.10 L0.6 0.99 L0.45 1.10 L0.51 0.93 L0.37 0.82 L0.54 0.82 Z" fill="#ffffff"/>`,
  ),

  // Россия — белый/синий/красный (горизонтальные).
  ru: flag(hStripes('#ffffff', '#0039a6', '#d52b1e')),

  // Великобритания (gb) — Union Jack: синее поле + белый/красный кресты. (en→us с
  // 2026-06-21; gb оставлен как данные для возможного повторного использования.)
  gb: flag(
    `${field('#012169')}` +
      `<path d="M0 0 L3 2 M3 0 L0 2" stroke="#ffffff" stroke-width="0.4"/>` +
      `<path d="M0 0 L3 2 M3 0 L0 2" stroke="#c8102e" stroke-width="0.22"/>` +
      `<path d="M1.5 0 V2 M0 1 H3" stroke="#ffffff" stroke-width="0.55"/>` +
      `<path d="M1.5 0 V2 M0 1 H3" stroke="#c8102e" stroke-width="0.33"/>`,
  ),

  // Германия — чёрный/красный/золотой (горизонтальные).
  de: flag(hStripes('#000000', '#dd0000', '#ffce00')),

  // Испания — красный/жёлтый(шире)/красный.
  es: flag(
    `${field('#aa151b')}` + `<rect x="0" y="0.5" width="3" height="1" fill="#f1bf00"/>`,
  ),

  // Бразилия (pt→br) — зелёное поле + жёлтый ромб + синий круг.
  br: flag(
    `${field('#009b3a')}` +
      `<path d="M1.5 0.25 L2.75 1 L1.5 1.75 L0.25 1 Z" fill="#ffdf00"/>` +
      `<circle cx="1.5" cy="1" r="0.42" fill="#002776"/>`,
  ),

  // Италия — зелёный/белый/красный (вертикальные).
  it: flag(vStripes('#008c45', '#f4f9ff', '#cd212a')),

  // Франция — синий/белый/красный (вертикальные).
  fr: flag(vStripes('#0055a4', '#ffffff', '#ef4135')),

  // Китай (zh-CN) — красное поле + жёлтая звезда (упрощённо одна крупная).
  cn: flag(
    `${field('#de2910')}` +
      `<path d="M0.6 0.55 L0.72 0.9 L1.08 0.9 L0.79 1.12 L0.9 1.47 L0.6 1.25 L0.3 1.47 L0.41 1.12 L0.12 0.9 L0.48 0.9 Z" fill="#ffde00"/>`,
  ),

  // Япония (ja→jp) — белое поле + красный круг.
  jp: flag(`${field('#ffffff')}<circle cx="1.5" cy="1" r="0.6" fill="#bc002d"/>`),

  // Южная Корея (ko→kr) — белое поле + сине-красный таэгык (упрощённо).
  kr: flag(
    `${field('#ffffff')}` +
      `<circle cx="1.5" cy="1" r="0.5" fill="#cd2e3a"/>` +
      `<path d="M1.5 0.5 a0.25 0.25 0 0 1 0 0.5 a0.25 0.25 0 0 0 0 0.5 a0.5 0.5 0 0 1 0 -1 Z" fill="#0047a0"/>`,
  ),

  // Тайвань (zh-TW→tw) — красное поле + синий кантон + белое солнце (упрощённо).
  tw: flag(
    `${field('#fe0000')}` +
      `<rect x="0" y="0" width="1.5" height="1" fill="#000095"/>` +
      `<circle cx="0.75" cy="0.5" r="0.28" fill="#ffffff"/>`,
  ),

  // Вьетнам (vi→vn) — красное поле + жёлтая звезда.
  vn: flag(
    `${field('#da251d')}` +
      `<path d="M1.5 0.55 L1.63 0.92 L2.02 0.92 L1.71 1.15 L1.83 1.52 L1.5 1.29 L1.17 1.52 L1.29 1.15 L0.98 0.92 L1.37 0.92 Z" fill="#ffff00"/>`,
  ),

  // Турция (tr) — красное поле + белый полумесяц + белая звезда (упрощённо). Полумесяц
  // = большой белый круг минус смещённый красный круг (вырезка наложением); звезда —
  // справа от рога полумесяца. Тот же подход «поле + наложенные фигуры», что у jp/cn.
  tr: flag(
    `${field('#e30a17')}` +
      `<circle cx="1.35" cy="1" r="0.45" fill="#ffffff"/>` +
      `<circle cx="1.5" cy="1" r="0.36" fill="#e30a17"/>` +
      `<path d="M1.95 0.78 L2.03 1.0 L2.26 1.0 L2.08 1.14 L2.15 1.36 L1.95 1.22 L1.75 1.36 L1.82 1.14 L1.64 1.0 L1.87 1.0 Z" fill="#ffffff"/>`,
  ),

  // Индонезия (id) — две горизонтальные полосы: верх красный, низ белый. Тот же
  // примитив hStripes, что у прочих полосатых флагов.
  id: flag(hStripes('#ff0000', '#ffffff')),

  // Таиланд (th) — пять горизонтальных полос красная/белая/синяя(двойной высоты)/
  // белая/красная (канон-пропорции 1:1:2:1:1, синяя по центру вдвое выше). hStripes
  // даёт равные полосы, поэтому строим rect'ами вручную с явными высотами.
  th: flag(
    `<rect x="0" y="0" width="3" height="0.3333" fill="#a51931"/>` +
      `<rect x="0" y="0.3333" width="3" height="0.3333" fill="#f4f5f8"/>` +
      `<rect x="0" y="0.6667" width="3" height="0.6667" fill="#2d2a4a"/>` +
      `<rect x="0" y="1.3333" width="3" height="0.3333" fill="#f4f5f8"/>` +
      `<rect x="0" y="1.6667" width="3" height="0.3333" fill="#a51931"/>`,
  ),
};

/** Нейтральный глобус-плейсхолдер для неизвестного кода (defensive). */
const PLACEHOLDER = flag(
  `${field('#9aa7b8')}<circle cx="1.5" cy="1" r="0.7" fill="none" stroke="#ffffff" stroke-width="0.12"/>` +
    `<path d="M0.8 1 H2.2 M1.5 0.3 V1.7" stroke="#ffffff" stroke-width="0.1"/>`,
);

/**
 * Готовая SVG-разметка флага для инлайна (по ISO-коду из `LANG_FLAG`). Возвращает
 * строку `<svg…>…</svg>` с классом-якорем `nav-lang-flag-svg` (размер — из CSS).
 */
export function flagSvg(isoCode: string): string {
  return FLAGS[isoCode] ?? PLACEHOLDER;
}
